import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LANGS, LANG_CODES, PORTAL_TRANSLATIONS } from "./portalI18n";
import { portalApi } from "./portalApi";

const STORAGE_KEY = "portal.lang";
const RTL_LANGS = new Set(["ar"]);

// Defensive localStorage accessors — Firefox throws "The operation is
// insecure" when storage is gated (private browsing strict, cookies blocked,
// strict tracking protection on some Safari/iOS configs). We must not crash
// the portal first-paint if storage is unavailable.
function _safeLocalGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function _safeLocalSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch { /* storage disabled */ }
}

function detectInitialLang(serverPref) {
  if (typeof window === "undefined") return "en";
  const fromStorage = _safeLocalGet(STORAGE_KEY);
  if (fromStorage && LANG_CODES.includes(fromStorage)) return fromStorage;
  if (serverPref && LANG_CODES.includes(serverPref)) return serverPref;
  return "en";
}

const PortalLanguageContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
  dir: "ltr",
  isRTL: false,
  langs: LANGS,
});

export function PortalLanguageProvider({ children, user }) {
  const [lang, setLangState] = useState(() => detectInitialLang(user?.preferred_language));
  // Track the last server-preferred language we honoured so we don't
  // re-call setLangState() — and trigger consumer re-renders — when the
  // auth context replaces the user object with a new reference but the
  // same preferred_language string. Avoids feeding a re-render storm.
  const honouredServerLangRef = useRef(null);

  // Re-sync once we receive the user (initial render before /auth/me resolves).
  // Intentional set-state-in-effect: auth user arrives asynchronously after
  // first render and may carry the server-stored preference.
  useEffect(() => {
    if (!user?.preferred_language) return;
    if (!LANG_CODES.includes(user.preferred_language)) return;
    if (_safeLocalGet(STORAGE_KEY)) return; // explicit local choice wins
    if (honouredServerLangRef.current === user.preferred_language) return;
    honouredServerLangRef.current = user.preferred_language;
    setLangState((prev) => (prev === user.preferred_language ? prev : user.preferred_language));
  }, [user?.preferred_language]);

  // Apply dir + lang attributes to <html> so the whole portal flips.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    const prevDir = html.getAttribute("dir");
    const prevLang = html.getAttribute("lang");
    html.setAttribute("dir", RTL_LANGS.has(lang) ? "rtl" : "ltr");
    html.setAttribute("lang", lang);
    return () => {
      if (prevDir != null) html.setAttribute("dir", prevDir); else html.removeAttribute("dir");
      if (prevLang != null) html.setAttribute("lang", prevLang); else html.removeAttribute("lang");
    };
  }, [lang]);

  const setLang = useCallback(async (next) => {
    if (!LANG_CODES.includes(next)) return;
    setLangState(next);
    _safeLocalSet(STORAGE_KEY, next);
    try { await portalApi.updateMyLanguage(next); }
    catch (e) {
      // Server persistence failed (offline / 401) — local UI already updated.
      // Log a single line for diagnosability; the next /auth/me call will sync.
      console.warn("[portal-lang] could not persist language to server:", e?.message || e);
    }
  }, []);

  const t = useCallback(
    (key, fallback, fmt) => {
      const dict = PORTAL_TRANSLATIONS[lang] || PORTAL_TRANSLATIONS.en;
      let val = dict[key] ?? PORTAL_TRANSLATIONS.en[key] ?? fallback ?? key;
      if (fmt && typeof val === "string") {
        val = val.replace(/\{(\w+)\}/g, (_, k) => (fmt[k] != null ? String(fmt[k]) : `{${k}}`));
      }
      return val;
    },
    [lang],
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
      dir: RTL_LANGS.has(lang) ? "rtl" : "ltr",
      isRTL: RTL_LANGS.has(lang),
      langs: LANGS,
    }),
    [lang, setLang, t],
  );

  return (
    <PortalLanguageContext.Provider value={value}>
      {children}
    </PortalLanguageContext.Provider>
  );
}

export function usePortalLang() {
  return useContext(PortalLanguageContext);
}
